import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Plus, ArrowRight, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Sidebar widget: lists the user's created + joined Custom AI Challenges, and — the
 * moment they land here right after registering/logging in from the public AI Challenge
 * Builder widget — silently generates the challenge they asked for pre-signup
 * (see sessionStorage key "pending_challenge_topic", set in AIChallengeBuilderSection.js). */
export default function MyCustomChallengesWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const resumeAndLoad = async () => {
      const pendingTopic = sessionStorage.getItem("pending_challenge_topic");
      const pendingJoinId = sessionStorage.getItem("pending_join_challenge_id");
      if (pendingTopic) {
        sessionStorage.removeItem("pending_challenge_topic");
        setCreating(true);
        try {
          const { data: res } = await api.post("/custom-challenges", { topic: pendingTopic });
          toast.success(`"${res.challenge.title}" generated! Let's start Day 1.`);
          navigate(`/custom-challenges/${res.challenge.id}`);
          return;
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't generate your challenge — try again from the Marketplace.");
        } finally {
          setCreating(false);
        }
      } else if (pendingJoinId) {
        sessionStorage.removeItem("pending_join_challenge_id");
        setCreating(true);
        try {
          await api.post(`/custom-challenges/${pendingJoinId}/join`);
          navigate(`/custom-challenges/${pendingJoinId}`);
          return;
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't join that challenge — try again from the Marketplace.");
        } finally {
          setCreating(false);
        }
      }
      try {
        const { data: res } = await api.get("/custom-challenges/my");
        setData(res);
      } catch {
        setData({ created: [], joined: [] });
      }
    };
    resumeAndLoad();
  }, [navigate]);

  if (!data && !creating) return null;

  return (
    <div className="neu-card rounded-2xl p-6" data-testid="my-custom-challenges-widget">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
          <Wand2 className="w-3.5 h-3.5 text-[#B58BFF]" /> My Custom Challenges
        </span>
        <Link to="/marketplace#ai-challenge-builder" data-testid="my-custom-challenges-create-link" className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
          <Plus className="w-3 h-3" /> New
        </Link>
      </div>

      {creating && (
        <p className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Setting up your challenge...</p>
      )}

      {!creating && data?.joined?.length === 0 && (
        <p className="text-xs text-slate-400">No custom challenges yet — generate one on any skill (Python, LeetCode, AWS...) from the Marketplace.</p>
      )}

      {!creating && data?.joined?.length > 0 && (
        <div className="space-y-3">
          {data.joined.map((c) => (
            <Link key={c.id} to={`/custom-challenges/${c.id}`} data-testid={`my-custom-challenge-${c.id}`} className="flex items-center justify-between gap-2 group">
              <div className="min-w-0">
                <p className="text-sm text-white truncate group-hover:text-[#B58BFF] transition-colors">{c.title}</p>
                <p className="text-[11px] text-slate-500">
                  Day {c.my_current_day}/{c.total_days} · {c.my_completed_days} completed{!c.is_published && " · Draft"}
                </p>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-white transition-transform group-hover:translate-x-0.5 shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
